import { Dropdown } from 'antd';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Children } from 'react';

import {
  isMenuItemActive,
  renderMenuDefinition,
  useActiveRoute,
} from '../../utils/routeUtils';
import { usePageGuard } from '../guards/PageGuard';
import { useTopMenuStyles } from './TopMenu.styles';

function TopMenuItem({ disabled, className, children, ...rest }) {
  const { styles } = useTopMenuStyles();
  return (
    <div className={classNames(styles.menuItem, className)} {...rest}>
      {children}
    </div>
  );
}
TopMenuItem.innerClassName = 'TopMenu-MenuItemInner';

function MenuItem({
  component: C,
  className,
  labelId,
  to,
  children,
  disabled: disabledOuter,
  active: activeOuter,
  activeRoute,
  guard,
  ...rest
}) {
  const { enabled: allowed } = usePageGuard(guard);
  const disabled = disabledOuter || !allowed;
  const effectiveTo = disabled ? undefined : to;
  const Wrapper = effectiveTo ? Link : 'div';
  const active = activeOuter || (to && isMenuItemActive(activeRoute, { to }));

  return (
    <C
      key={labelId}
      className={classNames({ active, disabled }, className)}
      disabled={disabled}
      {...rest}
    >
      <Wrapper className={C.innerClassName} to={effectiveTo}>
        <FormattedMessage id={labelId} />
      </Wrapper>
    </C>
  );
}

function GuardedDropdownItem({ activeRoute, labelId, to, guard }) {
  const { enabled } = usePageGuard(guard);
  const disabled = !to || !enabled;
  const active = to && isMenuItemActive(activeRoute, { to });

  return {
    className: classNames({ active, disabled }),
    key: labelId,
    disabled,
    label: <SubMenuItem disabled={disabled} labelId={labelId} to={to} />,
  };
}

function SubMenuItem({ disabled, labelId, to }) {
  const effectiveTo = disabled ? undefined : to;
  const Wrapper = effectiveTo ? Link : 'div';

  return (
    <Wrapper to={effectiveTo}>
      <FormattedMessage id={labelId} />
    </Wrapper>
  );
}

function Submenu({ activeRoute, labelId, items }) {
  const { styles } = useTopMenuStyles();
  const dropdownMenuItems = Children.toArray(
    items.map(({ labelId: childLabelId, to, guard }) => (
      <GuardedDropdownItem
        activeRoute={activeRoute}
        guard={guard}
        labelId={childLabelId}
        to={to}
      />
    ))
  ).map(element => element.type(element.props));

  return (
    <Dropdown
      menu={{ items: dropdownMenuItems }}
      key={labelId}
      overlayClassName={styles.submenu}
      placement="bottomRight"
    >
      <MenuItem
        component={TopMenuItem}
        labelId={labelId}
        active={isMenuItemActive(activeRoute, { children: items })}
        className="has-arrow"
      />
    </Dropdown>
  );
}

function renderSubmenu(
  { labelId, children },
  _recurse,
  _data,
  { activeRoute }
) {
  return (
    <Submenu
      activeRoute={activeRoute}
      key={labelId}
      labelId={labelId}
      items={children}
    />
  );
}

function renderMenuItem({ labelId, to, guard }, { activeRoute }) {
  return (
    <MenuItem
      component={TopMenuItem}
      key={labelId}
      to={to}
      labelId={labelId}
      disabled={!to}
      activeRoute={activeRoute}
      guard={guard}
    />
  );
}

export default function TopMenu({ definitions }) {
  const activeRoute = get(useActiveRoute(definitions), 'to');
  const { styles } = useTopMenuStyles();
  const props = { activeRoute };

  return (
    <div className={styles.topMenu}>
      {renderMenuDefinition(definitions, {
        renderMenuItem,
        renderSubmenu,
        props,
      })}
    </div>
  );
}
